import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { IdPostCampaignDto } from '@/services/ws-post/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useNotification } from '@/js/libs/ui/src/V2';
import { useLazyRequest } from '@/js/libs/use-request';
import type { ApiParametersMethod } from '@/js/libs/use-request-legacy';

import { ErrorCode } from 'src/types.ts';

const POLLING_INTERVAL_MS = 3000;
/**
 * Most changes finish within the first minute, so the fast interval is what the user actually waits through. A change
 * that outlives it is slow anyway, and polling it every 3 s would spend ~200 requests on the full deadline.
 */
const SLOW_POLLING_INTERVAL_MS = 10000;
const FAST_POLLING_PERIOD_MS = 60 * 1000;
/**
 * A failed status poll says nothing about the change, which keeps running on the backend - reporting the first one as
 * a failure would fail a change that is about to succeed. Only a run of failed polls is reported.
 */
const MAX_FAILED_POLLS_IN_A_ROW = 3;
/**
 * Both kinds of change end in the same PATH availability query, whose own budget is `POLL_RETRIES_COUNT` 40 x
 * `POLL_RETRY_WAITING_TIME` 15 s (`PathApi.java:43-44`). Giving up earlier would call a change failed that the backend
 * is still allowed to finish.
 */
const DEADLINE_MS = 10 * 60 * 1000;
/**
 * Without this the global response interceptor navigates to `/404` on any 404 and throws the user off the page in the
 * middle of a save. The promise still rejects, which is what the polling catches.
 */
const POLLING_IGNORED_ERRORS = [ErrorCode.NotFound];

type CampaignApiParams = { campaignId: IdPostCampaignDto };

/**
 * The wait rejects with this when a change finished but its stored result could no longer be read. The change itself
 * was applied, so a caller showing state the result would have refreshed has to re-read it instead of keeping what it
 * shows - the user would otherwise save stale values back over a change that succeeded. The user is told about it by
 * the wait itself, so the caller reports nothing.
 */
export class AsyncChangeResultUnavailableError extends Error {
  constructor() {
    super('The change finished, but its stored result could no longer be read');
  }
}

export type AsyncChangeApiMethod<TData> = ApiParametersMethod<{ data: TData; responseCode: 200; headers: null }, CampaignApiParams>;

/** The three members every generated async change status enum has. */
interface AsyncChangeStatusValues<TStatus extends string> {
  PENDING: TStatus;
  DONE: TStatus;
  ERROR: TStatus;
}

interface UseWaitForAsyncChangeProps<TStatus extends string, TStatusDto extends { status: TStatus }, TResult> {
  statusApiMethod: AsyncChangeApiMethod<TStatusDto>;
  /** Left out by a change that stores no result of its own, such as a reconciliation. */
  resultApiMethod?: AsyncChangeApiMethod<TResult>;
  /**
   * The generated status enum itself. `StageChangeStatus` and `PositionsChangeStatus` are separate string enums, so
   * neither one of them nor a bare string literal can be compared against both.
   */
  statusValues: AsyncChangeStatusValues<TStatus>;
  /**
   * What an ERROR status means to the caller: returning normally aborts the wait quietly, throwing lets the caller's
   * own error handling report the failure.
   */
  onError: (statusDto: TStatusDto) => void;
  /**
   * Whether the caller reports the failures this hook rejects with. A caller that swallows the rejection - both stage
   * change buttons do - needs the request layer to report a status poll that keeps failing, which is what the default
   * leaves it to. A caller that reports it itself would otherwise show the same failure twice.
   */
  isFailureReportedByCaller?: boolean;
}

export type WaitForAsyncChange<TResult> = (campaignId: IdPostCampaignDto) => Promise<TResult>;

/**
 * Waits for a change a `202` answer moved off the request, by polling its status until it is `DONE` and then reading
 * the result it stored - the shared mechanics behind the stage change and the step 5 positions change.
 *
 * One wait at a time per component - the returned function keeps a single timer, and a second wait started while the
 * first one is still running would leave that one hanging on unmount. Every caller disables its button while waiting.
 * @returns A function resolving with the result of the finished change, rejecting with
 * `AsyncChangeResultUnavailableError` when the change was applied but its result is gone, and rejecting with
 * `undefined` on every other outcome - a failure the `onError` handler decided not to throw about, the deadline, or an
 * unmount. That quiet rejection is the repo's "cancelled" idiom: `ParentOrchestraForm` skips `onSubmitError` for a
 * thrown `null`/`undefined`. Every outcome but an unmount is notified about.
 */
export const useWaitForAsyncChange = <TStatus extends string, TStatusDto extends { status: TStatus }, TResult>({
  statusApiMethod,
  resultApiMethod,
  statusValues,
  onError,
  isFailureReportedByCaller = false,
}: UseWaitForAsyncChangeProps<TStatus, TStatusDto, TResult>): WaitForAsyncChange<TResult> => {
  const [t] = useTranslation(['postCampaign']);
  const notify = useNotification();
  const mountedRef = useRef(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const resolveWaitRef = useRef<(() => void) | null>(null);

  const statusRequest = useLazyRequest({
    apiMethod: statusApiMethod,
    // polls run one after another, and an aborted poll would only be retried
    cancelPreviousRequests: false,
    ignoreErrors: POLLING_IGNORED_ERRORS,
  });

  const resultRequest = useLazyRequest({
    // `useLazyRequest` needs an api method on every render. A change that stores no result never loads this request;
    // the status method only stands in so the hook call stays unconditional.
    apiMethod: (resultApiMethod ?? statusApiMethod) as AsyncChangeApiMethod<TResult>,
    cancelPreviousRequests: false,
    ignoreErrors: POLLING_IGNORED_ERRORS,
  });

  useEffect(
    () => () => {
      mountedRef.current = false;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // the cleared timer would never resolve the wait, leaving the caller of the hook hanging
      resolveWaitRef.current?.();
    },
    [],
  );

  const waitForAsyncChange = useCallbackRef<WaitForAsyncChange<TResult>>(async (campaignId) => {
    const startedAt = Date.now();
    const deadline = startedAt + DEADLINE_MS;
    const fastPollingUntil = startedAt + FAST_POLLING_PERIOD_MS;

    const wait = (delayMs: number) =>
      new Promise<void>((resolve) => {
        resolveWaitRef.current = resolve;
        timeoutRef.current = setTimeout(resolve, delayMs);
      });

    // false once the component is gone, so neither a notification nor a navigation reaches the user who left
    const waitForNextPoll = async () => {
      await wait(Date.now() < fastPollingUntil ? POLLING_INTERVAL_MS : SLOW_POLLING_INTERVAL_MS);
      return mountedRef.current;
    };

    const abortQuietly = (): Promise<TResult> => Promise.reject(undefined);

    const readResult = async (): Promise<TResult> => {
      if (!resultApiMethod) {
        // nothing was stored to read - the caller only waited for the change to be over
        return undefined as TResult;
      }

      try {
        const { data } = await resultRequest.load({ campaignId }, { silent: true });
        return mountedRef.current ? data : await abortQuietly();
      } catch {
        // the change is done, only its stored result is gone - a failure here is not a failed change
        if (!mountedRef.current) {
          return abortQuietly();
        }

        notify.info({ body: t('postCampaign:CAMPAIGN_CHANGE_RESULT_UNAVAILABLE') });

        throw new AsyncChangeResultUnavailableError();
      }
    };

    const poll = async (failedPollsInARow: number): Promise<TResult> => {
      let statusDto: TStatusDto;
      const isLastAllowedPoll = failedPollsInARow >= MAX_FAILED_POLLS_IN_A_ROW;

      try {
        // a retried poll stays silent; the last allowed one is reported the way every request is, unless the caller
        // reports the rejection itself
        ({ data: statusDto } = await statusRequest.load({ campaignId }, { silent: !isLastAllowedPoll || isFailureReportedByCaller }));
      } catch (error) {
        if (isLastAllowedPoll) {
          throw error;
        }

        return (await waitForNextPoll()) ? poll(failedPollsInARow + 1) : abortQuietly();
      }

      if (!mountedRef.current) {
        return abortQuietly();
      }

      if (statusDto.status === statusValues.PENDING) {
        if (Date.now() >= deadline) {
          notify.info({ body: t('postCampaign:CAMPAIGN_CHANGE_STILL_RUNNING') });
          return abortQuietly();
        }

        return (await waitForNextPoll()) ? poll(0) : abortQuietly();
      }

      if (statusDto.status === statusValues.ERROR) {
        onError(statusDto);
        return abortQuietly();
      }

      return readResult();
    };

    return (await waitForNextPoll()) ? poll(0) : abortQuietly();
  });

  return waitForAsyncChange;
};
